// ABOUTME: Banner component for order variation codes (RMC only).
// Supports two placements: 'top' (for UE/CC) and 'inline' (for DF/G-/G+).

import {ErrorOutline, WarningAmber} from '@mui/icons-material'
import {Box, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {Order} from '../Order.types'
import {getTopBannerContent, getInlineBannerContent} from '../variationCode.utils'

const topBannerStyles = {
  error: {
    backgroundColor: '#FEF2F2',
    borderColor: '#EF4444',
    titleColor: '#991B1B',
    Icon: ErrorOutline
  },
  warning: {
    backgroundColor: '#fef9f2',
    borderColor: '#ee8b00',
    titleColor: '#E65100',
    Icon: WarningAmber
  }
} as const

const inlineBannerStyles = {
  error: {
    backgroundColor: '#FEF2F2',
    borderColor: '#EF4444'
  },
  info: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6'
  }
} as const

interface VariationCodeBannerProps {
  order: Order
  placement: 'top' | 'inline'
}

export const VariationCodeBanner: React.FC<VariationCodeBannerProps> = ({order, placement}) => {
  const {t} = useTranslation()

  if (placement === 'top') {
    const content = getTopBannerContent(order, t)
    if (!content) return null

    const style = topBannerStyles[content.variant]
    const {Icon} = style

    return (
      <Box
        data-test-id={`variation-code-banner-top-${order.variationCode}`}
        sx={{
          backgroundColor: style.backgroundColor,
          borderLeft: `4px solid ${style.borderColor}`,
          padding: '16px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <Icon
            sx={{
              color: style.titleColor,
              fontSize: 24,
              flexShrink: 0
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#000000'
            }}
          >
            {content.title}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#000000',
            marginTop: '4px'
          }}
        >
          {content.description}
        </Typography>
      </Box>
    )
  }

  if (placement === 'inline') {
    const content = getInlineBannerContent(order, t)
    if (!content) return null

    const style = inlineBannerStyles[content.variant]

    return (
      <Box
        data-test-id={`variation-code-inline-banner-${order.variationCode}`}
        sx={{
          marginTop: '8px',
          padding: '16px',
          backgroundColor: style.backgroundColor,
          borderLeft: `4px solid ${style.borderColor}`
        }}
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#000000'
          }}
        >
          {'* '}{content.description}
        </Typography>
      </Box>
    )
  }

  return null
}
